/**
 * SubmissionChecklistPanel — the artifact ops actually files with
 * the bid. Pulls every "submit X / Annexure Y / declaration on stamp
 * paper" we could detect into a categorised checkable list with
 * per-item notes, a progress bar, and a PDF export button.
 *
 * Backed by /api/v2/rfp/{id}/submission-checklist (GET + PATCH) plus
 * .pdf for the printable export.
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Loader2, Download, Check, FileText, AlertCircle, Plus, Trash2, EyeOff, RotateCcw, ChevronDown, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { v2Api } from "@/lib/v2Api";


const CATEGORY_LABEL = {
  tender: "Tender Instruments",
  corporate: "Corporate Documents",
  financial: "Financial Documents",
  technical: "Technical / OEM Documents",
  legal: "Legal Declarations",
  personnel: "Personnel & Project",
  annexures: "Annexure Formats",
  custom: "Custom Items",
};


export default function SubmissionChecklistPanel({ rfpId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [customLabel, setCustomLabel] = useState("");
  const [showDismissed, setShowDismissed] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!rfpId) return;
    setLoading(true);
    try {
      const d = await v2Api.rfp.submissionChecklist(rfpId);
      setData(d);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Failed");
    } finally {
      setLoading(false);
    }
  }, [rfpId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const patch = async (payload) => {
    setBusyKey(payload.key);
    try {
      const fresh = await v2Api.rfp.patchSubmissionChecklist(rfpId, payload);
      setData(fresh);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setBusyKey(null);
    }
  };

  const toggleItem = (it) => patch({ key: it.key, checked: !it.checked });
  const setNote = (it, note) => patch({ key: it.key, note });
  const removeItem = (it) => patch({ key: it.key, delete: true });
  const dismissItem = (it) => patch({ key: it.key, hidden: true });
  const restoreItem = (it) => patch({ key: it.key, hidden: false });

  const addCustom = async () => {
    const label = customLabel.trim();
    if (!label) return;
    const key = "custom_" + Date.now().toString(36);
    await patch({ key, label, category: "custom", custom: true, checked: false });
    setCustomLabel("");
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Building checklist…
      </div>
    );
  }
  if (error) {
    return (
      <div className="m-4 p-3 rounded border border-rose-200 bg-rose-50 text-sm text-rose-800">
        Couldn't load checklist: {error}
      </div>
    );
  }
  if (!data || !data.groups?.length) {
    return (
      <div className="m-4 p-6 rounded border bg-amber-50 border-amber-200 text-sm text-amber-900 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 mt-0.5" />
        <div>
          No submission requirements detected. Re-analyse this tender, or
          add custom items below.
          <CustomAdder
            value={customLabel} onChange={setCustomLabel} onAdd={addCustom}
          />
        </div>
      </div>
    );
  }

  const { summary, groups, hidden_items = [] } = data;
  const pdfUrl = v2Api.rfp.submissionChecklistPdfUrl(rfpId);

  return (
    <div className="p-4 space-y-4">
      <div className="rounded-lg border bg-white p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900">
              Submission Checklist
            </div>
            <div className="text-xs text-slate-500">
              Hand this to ops on filing day. Tick what's ready,
              annotate gaps, then export as PDF for the file cover.
            </div>
          </div>
          <Button
            variant="outline" size="sm" className="gap-1.5"
            onClick={() => {
              // open via fetch+blob to attach the Authorization header
              // — a plain anchor would 401 because of bearer auth.
              fetch(pdfUrl, {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
                },
              })
                .then((r) => r.ok ? r.blob() : Promise.reject(r))
                .then((blob) => {
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "submission_checklist.pdf";
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                })
                .catch(() => toast.error("PDF export failed"));
            }}
          >
            <Download className="w-3.5 h-3.5" /> Export PDF
          </Button>
        </div>

        <div className="mt-3 flex items-center gap-2">
          <Progress value={summary.progress_pct} className="flex-1" />
          <span className="text-xs tabular-nums text-slate-600 shrink-0">
            {summary.checked}/{summary.total} · {summary.progress_pct}%
          </span>
        </div>
      </div>

      {groups.map((grp) => (
        <div key={grp.category} className="rounded-lg border bg-white">
          <div className="px-4 py-2 border-b bg-slate-50 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-800">
              {CATEGORY_LABEL[grp.category] || grp.category}
            </div>
            <Badge variant="outline" className="text-[11px]">
              {grp.checked}/{grp.total}
            </Badge>
          </div>
          <ul className="divide-y">
            {grp.items.map((it) => (
              <li key={it.key} className="px-4 py-2.5 flex items-start gap-3">
                <button
                  onClick={() => toggleItem(it)}
                  disabled={busyKey === it.key}
                  className={`mt-0.5 w-5 h-5 shrink-0 rounded border flex items-center justify-center transition
                    ${it.checked
                      ? "bg-emerald-600 border-emerald-600 text-white"
                      : "bg-white border-slate-300 hover:border-slate-500"}
                    ${busyKey === it.key ? "opacity-50" : ""}`}
                  aria-label={`Toggle ${it.label}`}
                >
                  {it.checked && <Check className="w-3.5 h-3.5" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm ${it.checked ? "line-through text-slate-400" : "text-slate-900"}`}>
                    {it.label}
                    {!it.auto && (
                      <span className="ml-1.5 text-[10px] text-slate-400">(custom)</span>
                    )}
                  </div>
                  {it.evidence && (
                    <div className="text-[11px] text-slate-500 italic mt-0.5 line-clamp-1">
                      “{it.evidence}”
                    </div>
                  )}
                  <NoteInput
                    initial={it.note} onSave={(n) => setNote(it, n)}
                    placeholder="Add a note (location, owner, status)…"
                  />
                </div>
                {it.auto ? (
                  <button
                    onClick={() => dismissItem(it)}
                    disabled={busyKey === it.key}
                    className="shrink-0 text-slate-300 hover:text-slate-500 transition"
                    title="Dismiss — not applicable for this tender"
                  >
                    <EyeOff className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <button
                    onClick={() => removeItem(it)}
                    className="shrink-0 text-slate-400 hover:text-rose-600"
                    title="Remove custom item"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}

      {hidden_items.length > 0 && (
        <div className="rounded-lg border bg-slate-50">
          <button
            type="button"
            onClick={() => setShowDismissed((v) => !v)}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-500 hover:text-slate-700"
          >
            {showDismissed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            <EyeOff className="w-3.5 h-3.5" />
            {hidden_items.length} dismissed item{hidden_items.length !== 1 ? "s" : ""}
          </button>
          {showDismissed && (
            <ul className="divide-y border-t">
              {hidden_items.map((it) => (
                <li key={it.key} className="px-4 py-2.5 flex items-center gap-3 opacity-50">
                  <div className="flex-1 min-w-0 text-sm text-slate-500 line-through truncate">
                    {it.label}
                  </div>
                  <button
                    onClick={() => restoreItem(it)}
                    disabled={busyKey === it.key}
                    className="shrink-0 text-slate-400 hover:text-emerald-600 transition"
                    title="Restore"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="rounded-lg border bg-white p-3">
        <div className="text-xs font-semibold text-slate-700 mb-1.5">
          Add a custom item
        </div>
        <CustomAdder
          value={customLabel} onChange={setCustomLabel} onAdd={addCustom}
        />
      </div>
    </div>
  );
}


function NoteInput({ initial, onSave, placeholder }) {
  const [value, setValue] = useState(initial || "");
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setValue(initial || ""); setDirty(false); }, [initial]);

  return (
    <div className="mt-1 flex items-center gap-1.5">
      <Input
        value={value}
        onChange={(e) => { setValue(e.target.value); setDirty(true); }}
        onBlur={() => { if (dirty) { onSave(value); setDirty(false); } }}
        placeholder={placeholder}
        className="h-7 text-xs"
      />
    </div>
  );
}


function CustomAdder({ value, onChange, onAdd }) {
  return (
    <div className="flex items-center gap-2">
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") onAdd(); }}
        placeholder="e.g. EMD demand draft from XYZ Bank"
        className="h-8 text-sm"
      />
      <Button size="sm" onClick={onAdd} disabled={!value.trim()}>
        <Plus className="w-3.5 h-3.5 mr-1" /> Add
      </Button>
    </div>
  );
}
